import { cva, type VariantProps } from "class-variance-authority"
import type { DrawerRootProps } from "reka-ui"
import type { InjectionKey, Ref } from "vue"

export { default as Sheet } from "./Sheet.vue"
export { default as SheetClose } from "./SheetClose.vue"
export { default as SheetContent } from "./SheetContent.vue"
export { default as SheetDescription } from "./SheetDescription.vue"
export { default as SheetFooter } from "./SheetFooter.vue"
export { default as SheetHeader } from "./SheetHeader.vue"
export { default as SheetTitle } from "./SheetTitle.vue"
export { default as SheetTrigger } from "./SheetTrigger.vue"

export const sheetVariants = cva(
  "fixed z-[70] gap-4 bg-secondary-200 px-6 shadow-lg transition ease-in-out [transform:translate(var(--drawer-swipe-movement-x,0px),var(--drawer-swipe-movement-y,0px))] data-[swiping]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 pb-6 pt-[max(theme(spacing.6),env(safe-area-inset-top))] rounded-b-md data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 pt-6 pb-[max(theme(spacing.6),env(safe-area-inset-bottom))] rounded-t-md data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-[calc(100svh-theme(spacing.6)-env(safe-area-inset-top)-env(safe-area-inset-bottom))] py-6 w-3/4 rounded-r-md data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-[calc(100svh-theme(spacing.6)-env(safe-area-inset-top)-env(safe-area-inset-bottom))] py-6 w-3/4 rounded-l-md data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
      size: {
        auto: "max-h-[calc(100vh-theme(spacing.6)-env(safe-area-inset-top))] supports-[height:100svh]:max-h-[calc(100svh-theme(spacing.6)-env(safe-area-inset-top))]",
        full: "h-[calc(100vh-theme(spacing.6)-env(safe-area-inset-top))] supports-[height:100svh]:h-[calc(100svh-theme(spacing.6)-env(safe-area-inset-top))]",
      },
    },
    defaultVariants: {
      side: "right",
      size: "auto",
    },
  },
)

export type SheetVariants = VariantProps<typeof sheetVariants>

export const sheetHandleVariants = cva(
  "absolute rounded-full bg-secondary-400",
  {
    variants: {
      side: {
        top: "bottom-2 left-1/2 h-1.5 w-12 -translate-x-1/2",
        bottom: "left-1/2 top-2 h-1.5 w-12 -translate-x-1/2",
        left: "right-2 top-1/2 h-12 w-1.5 -translate-y-1/2",
        right: "left-2 top-1/2 h-12 w-1.5 -translate-y-1/2",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
)

export const swipeDirectionBySide: Record<
  NonNullable<SheetVariants["side"]>,
  NonNullable<DrawerRootProps["swipeDirection"]>
> = {
  top: "up",
  bottom: "down",
  left: "left",
  right: "right",
}

export const sheetSwipeDirectionKey: InjectionKey<
  Ref<DrawerRootProps["swipeDirection"]>
> = Symbol("sheetSwipeDirection")
